import { appModules } from '@/app/app.module';
import type { MenuItem, MenuSection } from '@/app/app-module.types';
import { hasAnyRole } from '@/shared/guards/roles';

/**
 * Grupos rotulados da sidebar, na ordem da tela. O `main` fica fora: não tem
 * rótulo, e o "Menu" do topo é o cabeçalho da sidebar inteira.
 *
 * Grupo não tem `roles`, de propósito: ele aparece se algum item dele é
 * visível. Um gate no grupo seria um segundo a manter em sincronia com o dos
 * itens, e o modo de falhar dele é esconder tela que o papel libera.
 */
export const MENU_GROUPS: {
  section: Exclude<MenuSection, 'main'>;
  label: string;
}[] = [
  { section: 'operations', label: 'Operação' },
  { section: 'commercial', label: 'Comercial' },
  { section: 'registry', label: 'Cadastros' },
];

// Menu derivado dos manifestos dos módulos, ordenado. O filtro por seção e por
// papel acontece em runtime (depende do usuário logado).
export const allMenuItems: MenuItem[] = appModules
  .flatMap((module) => module.menu ?? [])
  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

export function visibleItems(
  section: MenuSection,
  roles: string[],
): MenuItem[] {
  return allMenuItems.filter(
    (item) =>
      (item.section ?? 'main') === section &&
      hasAnyRole(roles, item.roles ?? []),
  );
}

// Comprimento do match de `to` contra o pathname atual, ou -1 se não bate.
// `to === pathname` ou `pathname` é subrota de `to` (`${to}/...`).
function menuMatchLength(pathname: string, to: string): number {
  if (pathname === to) return to.length;
  const prefix = to.endsWith('/') ? to : `${to}/`;
  return pathname.startsWith(prefix) ? to.length : -1;
}

// Ativo apenas no item cujo `to` é o match mais específico (mais longo) para
// o pathname atual — evita que "Vendas" (`/sales`) e "Webhook Inbox"
// (`/sales/webhook-inbox`) fiquem marcados ao mesmo tempo, e ainda mantém
// "Vendas" ativo em subrotas não mapeadas no menu (ex.: `/sales/:id/:tab`).
export function isMenuItemActive(pathname: string, item: MenuItem): boolean {
  const length = menuMatchLength(pathname, item.to);
  if (length < 0) return false;
  return allMenuItems.every(
    (other) => menuMatchLength(pathname, other.to) <= length,
  );
}
